import { z } from 'zod'
import { emailKindSchema } from '../enums/email-kind.enum.js'
import { emailStatusSchema } from '../enums/email-status.enum.js'
import { resendEmailEventSchema } from '../enums/resend-email-event.enum.js'
import { instantSchema } from '../validators/temporal.validator.js'

/**
 * One message this system sent, as the record of it.
 *
 * Every send writes one of these — accepted, refused by the provider, or
 * skipped because no mail key is configured — and the provider's webhook moves
 * `status` on to `delivered`, `bounced` and the rest. It is the answer to "did
 * they actually get it?", which until now could only be had from a shell.
 */
export const emailLogDtoSchema = z
  .object({
    id: z.string().uuid(),
    kind: emailKindSchema,
    to: z.string(),
    from: z.string(),
    subject: z.string(),
    status: emailStatusSchema,
    /**
     * The message as it was sent, with credentials redacted.
     *
     * Null for anything sent before the log kept bodies, and for a send that
     * failed before it was rendered. A reader must handle its absence rather than
     * assume every row can be previewed.
     */
    html: z.string().nullable(),
    /**
     * The body is kept back from this reader: the email was one message to a
     * vendor about several events, and some are another organization's, which
     * this one may not read (docs/planner-vendors.md). Its subject and date are
     * still theirs to see. Defaulted while an older API is still answering.
     */
    withheld: z.boolean().default(false),
    /** The provider's last recorded event, and when — null until the webhook speaks. */
    lastEvent: resendEmailEventSchema.nullable(),
    lastEventAt: instantSchema.nullable(),
    /** Why it failed, when it did. */
    error: z.string().nullable(),
    sentAt: instantSchema,
  })
  // A withheld body is never shown, whatever the row carries: the preview opens
  // whenever there is HTML, and a withheld one is another organization's.
  .transform((log) => (log.withheld ? { ...log, html: null } : log))

export type EmailLogDto = z.infer<typeof emailLogDtoSchema>

export const emailLogListDtoSchema = z.object({
  emails: z.array(emailLogDtoSchema),
})

export type EmailLogListDto = z.infer<typeof emailLogListDtoSchema>
